import { isArray } from 'lodash';

import { getFloatCoordinates, REGEXP_LAT, REGEXP_LONG } from '../../../service/NavPointsUtils';

const MAX_TYNYINT = 255;
const MAX_SMALLINT = 32767;
const MAX_INT = 2147483647;
export const VALIDATION_EMPTY_FIELD = 'errors.validate.isRequired';
export const MAX_LATITUDE_INTEGER = 5;
export const MAX_LONGITUDE_INTEGER = 6;

const getRangeFromPrecision = (precision = []) => {
    const [int = 18, fraction = 0] = precision;
    let max = (Math.pow(10, int) - 1) / Math.pow(10, fraction);

    return [-max, max];
};

export const FieldTypes = {
    DATE: 'date',
    MOMENT: 'moment',
    NUMBER: 'number',
    STRING: 'string',
};

export const ValidatorTypes = {
    DECIMAL: 'decimal',
    INT: 'int',
    SMALLINT: 'smallint',
    TEMPLATE: 'template',
    TEXT: 'text',
    TINYINT: 'tinyint',
    LATITUDE: 'latitude',
    LONGITUDE: 'longitude',
};

const checkType = (type, value) => {
    if (type === FieldTypes.DATE) {
        return typeof value.getMonth === 'function';
    }

    if (type === FieldTypes.MOMENT) {
        return typeof value.seconds === 'function';
    }

    if (type === FieldTypes.NUMBER) {
        return Number.isInteger(Number(value)) || !isNaN(parseFloat(value));
    }

    if (type === FieldTypes.STRING) {
        return typeof value === 'string';
    }

    return false;
};

const checkLength = (strValue, length, translate) => {
    const isLengthArray = isArray(length);

    if (isLengthArray) {
        const maxLength = length[1];
        const minLength = length[0];

        if (strValue.length < minLength || strValue.length > maxLength) {
            return translate('errors.validate.lengthRange', { minLength, maxLength });
        }
    } else {
        if (strValue.length > length) {
            return translate('errors.validate.maxLength', { length });
        }
    }
};

const checkRange = ({ type, min: minValue, max: maxValue, limit, length, precision = [] }, fieldValue, translate) => {
    if (limit) {
        if (isArray(limit)) {
            minValue = limit[0];
            maxValue = limit[1];
        } else {
            minValue = 0;
            maxValue = limit;
        }
    }

    const value = +fieldValue;
    const strValue = value + '';
    const [, maxFraction = 0] = precision;
    let min, max;

    const decimal = (strValue.indexOf('.') !== -1 && strValue.substring(strValue.indexOf('.') + 1, strValue.length)) || '';

    switch (type) {
        case ValidatorTypes.TINYINT:
            min = 0;
            max = MAX_TYNYINT;
            break;
        case ValidatorTypes.SMALLINT:
            min = -MAX_SMALLINT - 1;
            max = MAX_SMALLINT;
            break;
        case ValidatorTypes.INT:
            min = -MAX_INT - 1;
            max = MAX_INT;
            break;
        case ValidatorTypes.DECIMAL:
            [min, max] = getRangeFromPrecision(precision);
            break;
    }

    min = minValue !== undefined ? Math.max(min, minValue) : min;
    max = maxValue !== undefined ? Math.min(max, maxValue) : max;

    if (min !== undefined && max !== undefined && !(value >= min && value <= max)) {
        return translate('errors.validate.range', { min, max });
    }

    if (length) {
        return checkLength(strValue, length, translate);
    }

    if (type === 'decimal' && decimal.length > maxFraction) {
        return translate('errors.validate.maxPrecision', { maxFraction });
    }

    return null;
};

const validateField = (validator = {}, fieldValue, required = false, translate) => {
    if (fieldValue === undefined || fieldValue === '' || fieldValue === null) {
        return validator.isRequired || required ? translate(VALIDATION_EMPTY_FIELD) : null;
    }

    switch (validator.type) {
        case ValidatorTypes.DECIMAL:
        case ValidatorTypes.TINYINT:
        case ValidatorTypes.SMALLINT:
        case ValidatorTypes.INT:
            return checkRange(validator, fieldValue, translate);
        case ValidatorTypes.TEXT:
            return !validator.length || checkLength(fieldValue + '', validator.length, translate);
        case ValidatorTypes.TEMPLATE:
            return !validator.template ? null : validator.template.test(fieldValue) ? null : translate('errors.validate.fieldNotValid');
        case ValidatorTypes.LATITUDE:
            if (!REGEXP_LAT.test(fieldValue)) {
                return translate('errors.validate.formatLatitude');
            }
            if (fieldValue.length >= MAX_LATITUDE_INTEGER) {
                const floatValue = getFloatCoordinates(fieldValue);
                return checkRange({ ...validator, type: ValidatorTypes.DECIMAL }, floatValue, translate);
            }

            return null;
        case ValidatorTypes.LONGITUDE:
            if (!REGEXP_LONG.test(fieldValue)) {
                return translate('errors.validate.formatLongitude');
            }
            if (fieldValue.length >= MAX_LONGITUDE_INTEGER) {
                const floatValue = getFloatCoordinates(fieldValue);
                return checkRange({ ...validator, type: ValidatorTypes.DECIMAL }, floatValue, translate);
            }

            return null;
        default:
            return null;
    }
};

export const validateState = (validator = {}, fieldValue, required = false, translate) => {
    const errorText = validateField(validator, fieldValue, validator.isRequired || required, translate);
    return errorText ? { errorText } : null;
};

export const checkField = (fields, name, value, translate) => {
    const fieldParams = fields[name];
    if (!fieldParams) {
        return '';
    }
    const { validator, title = '', type, required = true } = fieldParams || {};
    const { min, max, reg } = validator || {};

    if (required && (value === null || value === undefined)) {
        return translate('errors.validate.enterField', { title });
    }

    if (value && !checkType(type, value)) {
        return translate('errors.validate.fieldNotType', { title, type });
    }

    if (fieldParams.validator) {
        const validationError = validateField(fieldParams.validator, value, fieldParams.required, translate);
        if (validationError) {
            return validationError;
        }
    }

    if (fieldParams.type === ValidatorTypes.LATITUDE || fieldParams.type === ValidatorTypes.LONGITUDE) {
        return '';
    }

    if (min && value && value.length < min) {
        return translate('errors.validate.fieldLeastSymbols', { title, min });
    }
    if (max && value && value.length > max) {
        return translate('errors.validate.fieldMoreSymbols', { title, max });
    }

    if (reg && value && !value.match(reg)) {
        return translate('errors.validate.fieldIncorrect', { title });
    }

    return '';
};

export const validate = (data, checkField) => {
    return Object.keys(data).reduce((res, key) => {
        const error = checkField(key, data[key]);
        if (error) {
            res[key] = error;
        }

        return res;
    }, {});
};

export const getInitialData = (fields) => {
    return Object.keys(fields).reduce((res, key) => {
        res[key] = undefined;
        return res;
    }, {});
};

export const getValue = (validator = {}, fieldValue) => {
    const type = (validator && validator.type) || 'text';
    let value = fieldValue || value;

    switch (type) {
        case 'decimal':
            value = parseFloat(fieldValue);
            if ((!isNaN(value) && isFinite(value)) || value === 0) {
                return value;
            }

            return undefined;
        case 'tinyint':
        case 'smallint':
        case 'int':
            value = parseInt(fieldValue);
            if ((!isNaN(value) && isFinite(value)) || value === 0) {
                return value;
            }

            return undefined;
    }

    return value || '';
};
